// TelcoTrack — construction diary work summary headline helpers (single source of truth)
// Pattern: {phase/status} [{object type}] u č.p. … / od … po …

import { getObjectTypeName } from '../constants/object-type-labels.constants';
import { normalizeObjectTypeForLookup } from '../constants/object-workflow/phase-codes';

const EXCAVATION_TYPES = new Set(['VYKOP', 'VYKOP_NOVY', 'VYKOP_METALIKA']);

/** Lowercase object noun after phase label — e.g. „Nepřipojené spojkoviště“. */
const HEADLINE_OBJECT_TYPE_INLINE: Record<string, string> = {
  ROZVADEC: 'rozvaděč',
  SPOJKOVISTE: 'spojkoviště',
  JAMA: 'montážní jáma',
  INFO: 'info',
  VYKOP_METALIKA: 'výkop (metalika)',
  VYKOP_NOVY: 'výkop',
  UNASSIGNED: 'nepřiřazený objekt',
};

/** Genitive for „u Budovy č.p. …“ — location carries the type noun. */
const HEADLINE_OBJECT_TYPE_GENITIVE: Record<string, string> = {
  BUDOVA: 'Budovy',
};

export function isDiaryExcavationObjectType(type: string | null | undefined): boolean {
  if (!type) return false;
  return EXCAVATION_TYPES.has(normalizeObjectTypeForLookup(type));
}

function extractPhaseIndex(phaseCode: string): number | null {
  const m = phaseCode.match(/\.PHASE\.(\d+)(?:\b|$)/i);
  if (!m) return null;
  const n = Number.parseInt(m[1], 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * Věta pro výkopové fáze — místo surového „Vykopaný od …“.
 * VYKOP je lineární: 0 Nevykopaný → 1 Vykopaný → 2 Finální → 3 Předaný.
 * Neznámá fáze → null (fallback na fázi + typ objektu).
 */
export function excavationHeadlineTemplate(phaseTo: string, objectType: string): string | null {
  const type = normalizeObjectTypeForLookup(objectType);
  const idx = extractPhaseIndex(phaseTo);
  if (idx == null) return null;

  if (!isDiaryExcavationObjectType(type)) return null;

  switch (idx) {
    case 1:
      return 'Výkop byl vykopán';
    case 2:
      return 'Ve výkopu se obnovily povrchy';
    case 3:
      return 'Povrchy byly předány';
    default:
      return null;
  }
}

export function getWorkSummaryObjectTypeGenitive(objectType: string): string | null {
  const normalized = normalizeObjectTypeForLookup(objectType);
  return HEADLINE_OBJECT_TYPE_GENITIVE[normalized] ?? null;
}

/** Základ nadpisu bez lokace — fáze + typ objektu (kromě výkopů se šablonou). */
export function buildWorkSummaryBaseLabel(
  phaseLabel: string,
  objectType: string,
  phaseTo?: string | null,
): string {
  const phaseCode = phaseTo?.trim() ?? '';
  const template = phaseCode ? excavationHeadlineTemplate(phaseCode, objectType) : null;
  if (template) return template;

  const normalized = normalizeObjectTypeForLookup(objectType);
  const phase = phaseLabel.trim() || '—';

  const inline = HEADLINE_OBJECT_TYPE_INLINE[normalized];
  if (inline) return `${phase} ${inline}`.trim();

  if (HEADLINE_OBJECT_TYPE_GENITIVE[normalized]) return phase;

  const typeName = getObjectTypeName(normalized);
  if (typeName && typeName !== objectType) {
    const lower = typeName.charAt(0).toLowerCase() + typeName.slice(1);
    return `${phase} ${lower}`.trim();
  }
  return phase;
}

/**
 * Doplní lokaci k základu — „u …“ pro krátkou trasu / bod, jinak „od … po …“.
 * U BUDOVy: „u Budovy č.p. …“.
 */
export function formatWorkSummaryLocationHeadline(
  baseLabel: string,
  objectType: string,
  fromLabel: string,
  toLabel: string,
  routeSpanM: number,
  routeHeadlineSpanMeters = 50,
): string {
  const genitive = getWorkSummaryObjectTypeGenitive(objectType);
  const useSpanFormat = routeSpanM > routeHeadlineSpanMeters;

  const pointSuffix = (loc: string): string =>
    genitive ? `u ${genitive} ${loc}` : `u ${loc}`;

  if (!useSpanFormat && fromLabel === toLabel) {
    return `${baseLabel} ${pointSuffix(fromLabel)}`.trim();
  }

  if (genitive) {
    return `${baseLabel} od ${genitive} ${fromLabel} po ${genitive} ${toLabel}`.trim();
  }
  return `${baseLabel} od ${fromLabel} po ${toLabel}`.trim();
}
